import { observer } from "mobx-react";
import RmaPageStore, { IRmaRestockAction } from "../../stores/rma-page-store";
import { DefaultModalLayout } from "../../../../../components/modal/layouts/default-modal-layout";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR } from "../../../../../components/fonts/font-props";
import { Button } from "../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import { useEffect } from "react";
import './rma-action-restock-modal.scss';
import { IOption, SelectDropdownGrouped } from "../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import RestockNote from "../restock-ntoe/restock-note";
import currentPortalStore from "../../../../../stores/current-portal-store";
import RestockNoteRefundProcess from "../restock-note-refund-process/restock-note-refund-process";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { SingleValue } from "react-select";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";

interface IProps {
    rma: RmaPageStore;
    rmaAction: IRmaRestockAction;
    onCloseClick: () => void;
}

const RmaActionRestockModal: React.FC<IProps> = observer(({rma, rmaAction, onCloseClick}) => {
    const actionStore = rmaAction.store;

    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-rma-card';
    const rmaCardPopups = 'dashboard-rma-card-popups';

    useEffect(() => {
	    rmsMixpanelStore.trackEvent("d&_rma_restock_button");
    }, []);

    useEffect(() => {
        if (rma.rmaReturnMethod?.shopify_restock_location_id) {
            actionStore.setRestockingLocationId(rma.rmaReturnMethod?.shopify_restock_location_id);
        }
    }, [actionStore, rma.rmaReturnMethod?.shopify_restock_location_id]);
    
    const selectedProductCasesNames = rma.selectedProductCases.map(pc => {
        const product = pc.productStore;
        return `${product.name}${product.variantTitle ? ` ${product.variantTitle}` : ''}`;
    });

    actionStore.setSelectedProductCaseIds(rma.selectedProductCases.map(pc => pc.id));

    const restockingLocationOptions = rma.storeLocationsOptions(getTranslation);
    const selectedRestockingLocation = () => {
        let selectedOption = undefined;
        restockingLocationOptions.forEach(og => {
            const selected = og.options.find(o => o.value === actionStore.restockingLocationId);
            if (selected) {
                selectedOption = selected;
            }
        });
        return selectedOption;
    }

    const onSubmit = () => {
        actionStore.submitAction(onCloseClick);
    };

    const noneRestockableItemsCount = rma.getNoneRestockableSelectedItemsCount();
    const onSelectedRestockingLocationChange = (v: SingleValue<IOption>) => {
        actionStore.setRestockingLocationId(v?.value as string);
    }

    return (
        <DefaultModalLayout isCloseDisabled={true} onCloseClicked={() => {}}>
            <div className="restock-action-modal-content">
                <FontBody1 color={FONT_COLOR.NEUTRAL3}>{getTranslation(rmaAction.name, scope)}</FontBody1>
                { !!noneRestockableItemsCount &&
                    <RestockNote
                        noneRestockableItemsCount={noneRestockableItemsCount}
                        selectedItemsCount={rma.selectedProductCases.length}
                    />
                }
                <FontBody1 color={FONT_COLOR.PRIMARY3}>{ `${getTranslation(rmaAction.descriptionTranslationKey, scope)} ${selectedProductCasesNames?.join(', ')}` }</FontBody1>

                <SelectDropdownGrouped
                    options={restockingLocationOptions}
                    value={selectedRestockingLocation()}
                    onChange={onSelectedRestockingLocationChange}
                />
                {currentPortalStore?.setting?.showRefundNoteBeforeRestocking && (
                    <RestockNoteRefundProcess />
                )}
                <div className="restock-action-modal-buttons">
                    <Button text={getTranslation('CANCEL', rmaCardPopups)} onClick={onCloseClick} variant={BUTTON_VARIANTS.Tertiary} disabled={actionStore.isProcessing} />
                    <Button text={getTranslation(rmaAction.buttonTextTranslationKey, scope)} onClick={onSubmit} variant={BUTTON_VARIANTS.Primary} disabled={selectedRestockingLocation()===undefined} isLoading={actionStore.isProcessing} hasLoader={true} />
                </div>
            </div>            
        </DefaultModalLayout>
    );
});

export default RmaActionRestockModal;